
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';

interface PageSelectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  totalPages: number;
  totalTeachers: number;
  onDownload: (selectedPages: number[]) => void;
}

export const PageSelectionDialog = ({ 
  isOpen, 
  onClose, 
  totalPages, 
  totalTeachers, 
  onDownload 
}: PageSelectionDialogProps) => {
  const [selectedPages, setSelectedPages] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedPages(Array.from({ length: totalPages }, (_, i) => i + 1));
    } else {
      setSelectedPages([]);
    }
  };

  const handlePageSelect = (pageNumber: number, checked: boolean) => {
    if (checked) {
      setSelectedPages(prev => [...prev, pageNumber]);
    } else {
      setSelectedPages(prev => prev.filter(p => p !== pageNumber));
      setSelectAll(false);
    }
  };

  const handleDownload = () => {
    if (selectedPages.length === 0) return;
    onDownload(selectedPages);
    onClose();
  };

  const handleDownloadAll = () => {
    const allPages = Array.from({ length: totalPages }, (_, i) => i + 1);
    onDownload(allPages);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg mx-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Download Teachers List</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="text-sm text-muted-foreground">
            Your teachers list contains <strong>{totalTeachers} teachers</strong> across <strong>{totalPages} pages</strong> (20 teachers per page).
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="select-all"
                checked={selectAll}
                onCheckedChange={handleSelectAll}
              />
              <label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
                Select All Pages
              </label>
            </div>
            
            <ScrollArea className="h-40 border rounded-md p-3">
              <div className="space-y-2">
                {Array.from({ length: totalPages }, (_, i) => {
                  const pageNumber = i + 1;
                  const startTeacher = (i * 20) + 1;
                  const endTeacher = Math.min((i + 1) * 20, totalTeachers);
                  
                  return (
                    <div key={pageNumber} className="flex items-center space-x-2">
                      <Checkbox 
                        id={`page-${pageNumber}`}
                        checked={selectedPages.includes(pageNumber)}
                        onCheckedChange={(checked) => 
                          handlePageSelect(pageNumber, checked as boolean)
                        }
                      />
                      <label htmlFor={`page-${pageNumber}`} className="text-sm cursor-pointer">
                        Page {pageNumber} (Teachers {startTeacher}-{endTeacher})
                      </label>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        </div>

        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            variant="secondary" 
            onClick={handleDownloadAll}
          >
            Download All Pages
          </Button>
          <Button 
            onClick={handleDownload}
            disabled={selectedPages.length === 0}
          >
            Download Selected ({selectedPages.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
